// Лёгкий фейерверк на canvas, без зависимостей.
// Ракеты взлетают снизу и разрываются на искры с гравитацией, сопротивлением
// воздуха и мерцанием. Когда всё догорело — цикл requestAnimationFrame
// останавливается сам и вызывает onIdle.
const TAU = Math.PI * 2;

// Палитры в цветах приложения: золото (кнопки/деньги), мята, голубой, коралл, фиолетовый
const PALETTES = [
    ['#FFE193', '#F5DB31', '#FFB800', '#FFF6CC'],
    ['#6DCEAE', '#00A597', '#B8F5E2', '#E9FFF7'],
    ['#7CC4FF', '#1696EC', '#D6ECFF', '#FFFFFF'],
    ['#FF9A8B', '#FF5E7E', '#FFD1DC', '#FFF0F3'],
    ['#C9A7FF', '#8B5CF6', '#EBDDFF', '#FFFFFF']
];

// Точки разрывов (доли ширины/высоты экрана): над суммой и по бокам от неё
const SLOTS = [
    [0.24, 0.2], [0.76, 0.18], [0.5, 0.11], [0.15, 0.47],
    [0.85, 0.49], [0.3, 0.66], [0.7, 0.68], [0.5, 0.26]
];

const rand = (a, b) => a + Math.random() * (b - a);
const pick = (arr) => arr[(Math.random() * arr.length) | 0];

export function createFireworks(canvas, options) {
    const opts = options || {};
    const ctx = canvas && canvas.getContext ? canvas.getContext('2d') : null;
    if (!ctx) return null;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const density = opts.density || 1;
    const rockets = [];
    const sparks = [];
    const timers = [];
    let width = 0;
    let height = 0;
    let raf = 0;
    let last = 0;
    let running = false;
    let pending = 0;
    let destroyed = false;

    function resize() {
        width = canvas.clientWidth || window.innerWidth;
        height = canvas.clientHeight || window.innerHeight;
        canvas.width = Math.round(width * dpr);
        canvas.height = Math.round(height * dpr);
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    resize();
    window.addEventListener('resize', resize);

    function start() {
        if (running || destroyed) return;
        running = true;
        last = 0;
        raf = window.requestAnimationFrame(frame);
    }

    // power < 1 — маленький салют (искры у баланса, галочка «отчёт отправлен»)
    function explode(x, y, palette, kind, power) {
        const colors = palette || pick(PALETTES);
        const type = kind || pick(['peony', 'peony', 'ring', 'willow']);
        const p = Math.max(0.2, Math.min(1.2, power || 1));
        const count = Math.round((type === 'ring' ? 46 : 64) * density * (0.4 + 0.6 * p));
        const base = (type === 'willow' ? 175 : 235) * p;
        for (let i = 0; i < count; i++) {
            const angle = (i / count) * TAU + rand(-0.09, 0.09);
            const speed = type === 'ring' ? base * rand(0.94, 1.06) : base * Math.pow(Math.random(), 0.45) * rand(0.65, 1.25);
            sparks.push({
                x: x, y: y,
                vx: Math.cos(angle) * speed,
                vy: Math.sin(angle) * speed,
                age: 0,
                life: (type === 'willow' ? rand(1.4, 1.9) : rand(0.9, 1.35)) * (0.55 + 0.45 * p),
                color: Math.random() < 0.18 ? '#FFFFFF' : pick(colors),
                size: rand(1.5, 2.7) * (0.7 + 0.3 * p),
                drag: type === 'willow' ? 1.15 : 1.9,
                gravity: (type === 'willow' ? 270 : 175) * (0.5 + 0.5 * p),
                glitter: Math.random() < 0.3
            });
        }
        // короткая вспышка в точке разрыва
        sparks.push({ x: x, y: y, vx: 0, vy: 0, age: 0, life: 0.2, size: 20 * p, flash: true, drag: 0, gravity: 0 });
        start();
    }

    function launch(x, targetY, palette) {
        const g = 2600;                                  // «гравитация» ракеты, px/s²
        const startY = height + 10;
        const h = Math.max(140, startY - targetY);
        rockets.push({ x: x, y: startY, vx: rand(-35, 35), vy: -Math.sqrt(2 * g * h), g: g, targetY: targetY, palette: palette, trail: [] });
        start();
    }

    // i-я ракета залпа: первая — по центру, остальные по очереди слева и справа
    function scheduleRocket(i, delay) {
        pending++;
        const id = window.setTimeout(() => {
            pending--;
            const k = timers.indexOf(id);
            if (k >= 0) timers.splice(k, 1);
            if (destroyed) return;
            const side = i % 2 === 0 ? -1 : 1;
            const x = i === 0 ? width * rand(0.42, 0.58) : width * (0.5 + side * rand(0.14, 0.36));
            launch(x, height * rand(0.13, 0.36));
        }, delay);
        timers.push(id);
    }

    // count ракет с интервалом spacing мс
    function show(count, spacing) {
        for (let i = 0; i < count; i++) {
            scheduleRocket(i, i * (spacing || 220) + rand(0, 80));
        }
    }

    // Разрывы без ракет — сразу вокруг центра экрана, чтобы салют был виден,
    // пока на экране крупная сумма (ракете нужно ~0,7 с, чтобы взлететь).
    function scheduleBurst(delay, slot) {
        pending++;
        const id = window.setTimeout(() => {
            pending--;
            const k = timers.indexOf(id);
            if (k >= 0) timers.splice(k, 1);
            if (destroyed) return;
            explode(width * (slot[0] + rand(-0.05, 0.05)), height * (slot[1] + rand(-0.035, 0.035)), undefined, undefined, rand(0.75, 1));
        }, delay);
        timers.push(id);
    }

    function sprinkle(count, spacing, startDelay) {
        const slots = SLOTS.slice().sort(() => Math.random() - 0.5);
        for (let i = 0; i < count; i++) {
            scheduleBurst((startDelay || 0) + i * (spacing || 260) + rand(0, 60), slots[i % slots.length]);
        }
    }

    function step(dt) {
        for (let i = rockets.length - 1; i >= 0; i--) {
            const r = rockets[i];
            r.vy += r.g * dt;
            r.x += r.vx * dt;
            r.y += r.vy * dt;
            r.trail.push(r.x, r.y);
            if (r.trail.length > 20) r.trail.splice(0, 2);
            if (r.vy >= -45 || r.y <= r.targetY) {
                rockets.splice(i, 1);
                explode(r.x, r.y, r.palette);
            }
        }
        for (let i = sparks.length - 1; i >= 0; i--) {
            const s = sparks[i];
            s.age += dt;
            if (s.age >= s.life) {
                sparks.splice(i, 1);
                continue;
            }
            const k = Math.max(0, 1 - s.drag * dt);
            s.vx *= k;
            s.vy = s.vy * k + s.gravity * dt;
            s.x += s.vx * dt;
            s.y += s.vy * dt;
        }
    }

    function draw() {
        ctx.clearRect(0, 0, width, height);
        ctx.globalCompositeOperation = 'lighter';
        ctx.lineCap = 'round';
        for (let r = 0; r < rockets.length; r++) {
            const t = rockets[r].trail;
            for (let j = 2; j < t.length; j += 2) {
                const a = j / t.length;
                ctx.strokeStyle = 'rgba(255, 236, 170, ' + (a * 0.85).toFixed(3) + ')';
                ctx.lineWidth = 2.4 * a;
                ctx.beginPath();
                ctx.moveTo(t[j - 2], t[j - 1]);
                ctx.lineTo(t[j], t[j + 1]);
                ctx.stroke();
            }
        }
        for (let i = 0; i < sparks.length; i++) {
            const s = sparks[i];
            const t = s.age / s.life;
            if (s.flash) {
                const radius = s.size * (1 + t * 2.2);
                const g = ctx.createRadialGradient(s.x, s.y, 0, s.x, s.y, radius);
                g.addColorStop(0, 'rgba(255, 255, 255, ' + (1 - t).toFixed(3) + ')');
                g.addColorStop(1, 'rgba(255, 255, 255, 0)');
                ctx.globalAlpha = 1;
                ctx.fillStyle = g;
                ctx.beginPath();
                ctx.arc(s.x, s.y, radius, 0, TAU);
                ctx.fill();
                continue;
            }
            if (s.glitter && t > 0.35 && Math.random() < 0.4) continue;    // мерцание
            ctx.globalAlpha = t < 0.65 ? 1 : Math.max(0, 1 - (t - 0.65) / 0.35);
            ctx.strokeStyle = s.color;
            ctx.lineWidth = s.size;
            ctx.beginPath();
            ctx.moveTo(s.x - s.vx * 0.04, s.y - s.vy * 0.04);              // хвост = размытие движения
            ctx.lineTo(s.x, s.y);
            ctx.stroke();
        }
        ctx.globalAlpha = 1;
        ctx.globalCompositeOperation = 'source-over';
    }

    function frame(now) {
        if (destroyed) return;
        const dt = last ? Math.min(0.034, (now - last) / 1000) : 0.016;
        last = now;
        step(dt);
        draw();
        if (rockets.length || sparks.length) {
            raf = window.requestAnimationFrame(frame);
        } else {
            running = false;
            ctx.clearRect(0, 0, width, height);
            if (!pending && opts.onIdle) opts.onIdle();
        }
    }

    function destroy() {
        destroyed = true;
        timers.forEach((id) => window.clearTimeout(id));
        timers.length = 0;
        window.cancelAnimationFrame(raf);
        window.removeEventListener('resize', resize);
        rockets.length = 0;
        sparks.length = 0;
        ctx.clearRect(0, 0, width, height);
    }

    return {
        show: show,
        sprinkle: sprinkle,
        launch: launch,
        burst: (x, y, palette, kind, power) => explode(x, y, palette, kind, power),
        size: () => ({ width: width, height: height }),
        isIdle: () => !rockets.length && !sparks.length && !pending,
        destroy: destroy
    };
}

export const FIREWORK_PALETTES = PALETTES;
